import React from 'react';
import Lightbox from 'react-awesome-lightbox';
import 'react-awesome-lightbox/build/style.css';
import {
    Search, ChevronDown, ChevronUp, X, SlidersHorizontal,
    Grid3X3, List,
} from 'lucide-react';
import i18n from '../../../../i18n';
import { getActiveEnrolled } from '../../../../models/groups';
import AuctionCardNew from '../../components/Auction/AuctionCard';
import AuctionGroupCardNew from '../../components/Auction/AuctionGroupCard';
import AuctionListCard from '../Auctions/components/AuctionListCard';
import '../../../../styles/components/auctions-page.scss';
import '../../../../styles/components/auction-list-card.scss';
import { SkeletonAuctionCard } from '../../../helpers/skeletons/SkeletonAuctionCard';

const current_lng = localStorage.getItem('I18N_LANGUAGE');

export class MyAuctions extends React.Component {
    constructor() {
        super();
        this.state = {
            auctions: [],
            allAuctions: [],
            is_loading: false,
            grid: true,
            input: { search_text: '' },
            statusFilter: 'all',
            sortBy: 'newest',
            showSortDropdown: false,
            openSections: { status: true },
            lightboxImages: [],
            is_active_light_box: false,
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        this.loadAuctions();
    }

    async loadAuctions() {
        this.setState({ is_loading: true });
        try {
            const response = await getActiveEnrolled('limit=100');
            if (response && response.data) {
                // Normalize the data so cards don't get NaN
                const normalized = response.data.map((auc) => ({
                    ...auc,
                    // Fields the cards expect but the enrolled API doesn't return
                    status_dis: auc.status_dis || auc.status || 'A',
                    current_amount: auc.latest_bid?.bid_amount || auc.current_amount || auc.start_amount || 0,
                    bid_count: auc.bidders_count || auc.bid_count || 0,
                    images: auc.images || [],
                    server_time: auc.server_time || new Date().toLocaleString('en-US', { timeZone: 'Asia/Muscat' }).replace(',', ''),
                    start_date_formatted: auc.start_date_formatted || auc.start_date || '',
                    end_date_formatted: auc.end_date_formatted || auc.end_date || '',
                    total_views: auc.total_views || 0,
                    is_a_group: auc.is_a_group || false,
                    is_enrolled: true,
                    category_name: auc.category_name || '',
                    // Organization info for cards
                    organizationDetails: auc.organization_relation
                        ? {
                            organization_name: auc.organization_relation.organization_name,
                            organization_name_ar: auc.organization_relation.organization_name_ar,
                            organization_image_url: auc.organization_relation.organization_image_url,
                            file_organization_image_full: auc.organization_relation.organization_image_url,
                        }
                        : auc.organizationDetails || null,
                    // Group info
                    group_name: auc.group_relation?.group_name || auc.group_name || '',
                    group_name_ar: auc.group_relation?.group_name_ar || auc.group_name_ar || '',
                    my_rank: auc.my_rank || null,
                }));

                this.setState({
                    allAuctions: normalized,
                    auctions: normalized,
                    is_loading: false,
                });
            }
        } catch (e) {
            console.error('Error loading enrolled auctions:', e);
            this.setState({ is_loading: false, auctions: [] });
        }
    }

    /* ── Filters ── */

    handleChange = (event) => {
        const input = { ...this.state.input, [event.target.name]: event.target.value };
        this.setState({ input }, this.applyFilters);
    };

    setStatusFilter = (status) => {
        this.setState({ statusFilter: status }, this.applyFilters);
    };

    setSortBy = (sortBy) => {
        this.setState({ sortBy, showSortDropdown: false }, this.applyFilters);
    };

    resetFilters = () => {
        this.setState(
            { statusFilter: 'all', input: { search_text: '' }, sortBy: 'newest' },
            this.applyFilters
        );
    };

    applyFilters = () => {
        let filtered = [...this.state.allAuctions];
        const { statusFilter, input, sortBy } = this.state;

        // Status filter
        if (statusFilter === 'active') {
            filtered = filtered.filter((a) => a.status_dis === 'A' || a.status === 'A');
        } else if (statusFilter === 'upcoming') {
            filtered = filtered.filter((a) => a.status_dis === 'U' || a.status === 'U');
        } else if (statusFilter === 'previous') {
            filtered = filtered.filter((a) => a.status_dis === 'E' || a.status === 'E');
        }

        // Search
        if (input.search_text && input.search_text.trim()) {
            const term = input.search_text.toLowerCase();
            filtered = filtered.filter(
                (a) =>
                    (a.title && a.title.toLowerCase().includes(term)) ||
                    (a.title_ar && a.title_ar.toLowerCase().includes(term))
            );
        }

        // Sort
        if (sortBy === 'newest') {
            filtered.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
        } else if (sortBy === 'oldest') {
            filtered.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
        }

        this.setState({ auctions: filtered });
    };

    getImagePopup = (active, images) => {
        const lightboxImages = (images || []).map((img) => ({ url: img.image }));
        this.setState({ is_active_light_box: active, lightboxImages });
    };

    toggleSection = (key) => {
        this.setState((prev) => ({
            openSections: { ...prev.openSections, [key]: !prev.openSections[key] },
        }));
    };

    render() {
        const {
            auctions, is_loading, grid, statusFilter, sortBy,
            showSortDropdown, openSections,
        } = this.state;

        const total = auctions.length;

        const SORT_OPTIONS = [
            { value: 'newest', label: i18n.t('newest')},
            { value: 'oldest', label: i18n.t('oldest')},
        ];

        const STATUS_OPTIONS = [
            { value: 'all', label: i18n.t('all_auctions')},
            { value: 'active', label: i18n.t('active_auctions')},
            { value: 'upcoming', label: i18n.t('upcoming_auctions')},
            { value: 'previous', label: i18n.t('previous_auctions')},
        ];

        return (
            <React.Fragment>
                <div className="mza-page">

                    {/* ══════════ RESULTS HEADER ══════════ */}
                    <div className="mza-results-header">
                        <div className="mza-results-header__inner">
                            <div className="mza-search-bar">
                                <Search size={18} className="mza-search-bar__icon" />
                                <input
                                    type="text"
                                    name="search_text"
                                    className="mza-search-bar__input"
                                    placeholder={i18n.t('search') || 'Search'}
                                    value={this.state.input.search_text || ''}
                                    onChange={this.handleChange}
                                    onKeyDown={(e) => e.key === 'Enter' && this.applyFilters()}
                                />
                            </div>
                            <button className="mza-search-bar__btn" onClick={this.applyFilters}>
                                {i18n.t('search') || 'Search'}
                            </button>

                            <span className="mza-results-header__count">
                                {is_loading ? '...' : total} {i18n.t('auctions') || 'auctions'}
                            </span>

                            <div className="mza-results-header__actions">
                                {(statusFilter !== 'all' || this.state.input.search_text) && (
                                    <button className="mza-results-header__reset" onClick={this.resetFilters}>
                                        <X size={14} />
                                        {i18n.t('reset_filters') || 'Reset filters'}
                                    </button>
                                )}
                                <div className="mza-sort">
                                    <button
                                        className="mza-sort__trigger"
                                        onClick={() => this.setState({ showSortDropdown: !showSortDropdown })}
                                    >
                                        <SlidersHorizontal size={14} />
                                        {i18n.t('sort_by') || 'Sort By'}
                                    </button>
                                    {showSortDropdown && (
                                        <div className="mza-sort__dropdown">
                                            {SORT_OPTIONS.map((opt) => (
                                                <button
                                                    key={opt.value}
                                                    className={`mza-sort__option ${sortBy === opt.value ? 'mza-sort__option--active' : ''}`}
                                                    onClick={() => this.setSortBy(opt.value)}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="mza-view-toggle">
                                    <button
                                        className={`mza-view-toggle__btn ${grid ? 'mza-view-toggle__btn--active' : ''}`}
                                        onClick={() => this.setState({ grid: true })}
                                    >
                                        <Grid3X3 size={16} />
                                    </button>
                                    <button
                                        className={`mza-view-toggle__btn ${!grid ? 'mza-view-toggle__btn--active' : ''}`}
                                        onClick={() => this.setState({ grid: false })}
                                    >
                                        <List size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ══════════ MAIN: Sidebar + Grid ══════════ */}
                    <div className="mza-main">
                        <div className="mza-main__inner">

                            {/* ── Sidebar ── */}
                            <aside className="mza-sidebar">
                                <div className="mza-sidebar__header">
                                    <h3 className="mza-sidebar__title">
                                        {i18n.t('filter_results') || 'Filter Results'}
                                    </h3>
                                </div>

                                {/* Status filter */}
                                <div className="mza-filter-group">
                                    <button
                                        className="mza-filter-group__header"
                                        onClick={() => this.toggleSection('status')}
                                    >
                                        <span>{i18n.t('auction_status') || 'Auction Status'}</span>
                                        {openSections.status ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                    </button>
                                    {openSections.status && (
                                        <div className="mza-filter-group__body">
                                            {STATUS_OPTIONS.map((opt) => (
                                                <label key={opt.value} className="mza-radio">
                                                    <input
                                                        type="radio"
                                                        name="status"
                                                        checked={statusFilter === opt.value}
                                                        onChange={() => this.setStatusFilter(opt.value)}
                                                    />
                                                    <span className="mza-radio__mark" />
                                                    <span className="mza-radio__label">{opt.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </aside>

                            {is_loading && (
                                <SkeletonAuctionCard />
                            )}
                            {/* ── Grid Area ── */}
                            <div className="mza-grid-area">

                                {!is_loading && auctions.length === 0 && (
                                    <div className="mza-empty">
                                        <Search size={48} />
                                        <p>{i18n.t('no_auctions_found') || 'No auctions found'}</p>
                                    </div>
                                )}

                                {/* Grid view */}
                                {!is_loading && grid && auctions.length > 0 && (
                                    <div className="mza-card-grid">
                                        {auctions.map((auction, i) =>
                                            !auction.is_a_group ? (
                                                <div key={auction.id || i} className="mza-card-grid__item">
                                                    <AuctionCardNew
                                                        auction={auction}
                                                        auctionId={auction.id}
                                                    />
                                                </div>
                                            ) : (
                                                <div key={auction.id || i} className="mza-card-grid__item">
                                                    <AuctionGroupCardNew
                                                        group={auction}
                                                        total_items={auctions.length}
                                                        can_bid={false}
                                                    />
                                                </div>
                                            )
                                        )}
                                    </div>
                                )}

                                {/* List view */}
                                {!is_loading && !grid && auctions.length > 0 && (
                                    <div className="mza-list-view">
                                        {auctions.map((auction, i) => (
                                            <AuctionListCard
                                                key={auction.id || i}
                                                auction={auction}
                                                getImagePopup={this.getImagePopup}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {this.state.is_active_light_box && (
                    <Lightbox
                        images={this.state.lightboxImages}
                        onClose={() => this.setState({ is_active_light_box: false })}
                    />
                )}
            </React.Fragment>
        );
    }
}